/**
 * 최신 뉴스레터 목록 위젯 — 블록 내부에서 개수/레이아웃/카테고리 인라인 편집
 * 공통 스타일: shared/style.scss (.ejs-widget)
 */
import api from '@/utils/api';

export default class RecentPostsTool {
  static get toolbox() {
    return {
      title: "최신 뉴스레터 목록",
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M16 6h3a1 1 0 0 1 1 1v11a2 2 0 0 1 -4 0v-13a1 1 0 0 0 -1 -1h-10a1 1 0 0 0 -1 1v12a3 3 0 0 0 3 3h11" /><path d="M8 8l4 0" /><path d="M8 12l4 0" /><path d="M8 16l4 0" /></svg>'
    };
  }

  static get isReadOnlySupported() { return true; }

  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;
    this.config = config || {};
    this.data = Object.assign({ count: 3, layout: "responsive", category_ext_id: null, category_name: null }, data || {});
    this.wrapper = null;
    this.editing = false;
    this.categories = [];
    this._categoriesLoaded = false;
  }

  render() {
    this.wrapper = document.createElement("div");
    this._renderView();
    if (!this.readOnly) {
      this.wrapper.addEventListener("click", () => { if (!this.editing) this._renderEdit(); });
      this._handleClickOutside = (e) => { if (this.editing && !this.wrapper.contains(e.target)) this._renderView(); };
      document.addEventListener("click", this._handleClickOutside, true);
    }
    return this.wrapper;
  }

  destroy() {
    if (this._handleClickOutside) document.removeEventListener("click", this._handleClickOutside, true);
  }

  async _fetchCategories() {
    if (this._categoriesLoaded) return;
    const spaceUrl = this.config.spaceUrl;
    if (!spaceUrl) return;
    try {
      const res = await api.get(`/${spaceUrl}/o/post_categories.json`);
      this.categories = res.data;
    } catch (e) {
      console.warn("[RecentPosts] Failed to fetch categories:", e);
    }
    this._categoriesLoaded = true;
  }

  _renderView() {
    this.editing = false;
    const layoutLabels = { grid: "카드", list: "리스트", responsive: "반응형" };
    const layoutLabel = layoutLabels[this.data.layout] || "반응형";
    const categoryLabel = this.data.category_name ? ` · ${this.data.category_name}` : "";
    this.wrapper.className = "ejs-widget";
    this.wrapper.style.cssText = "text-align: center; cursor: pointer;";
    this.wrapper.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M16 6h3a1 1 0 0 1 1 1v11a2 2 0 0 1 -4 0v-13a1 1 0 0 0 -1 -1h-10a1 1 0 0 0 -1 1v12a3 3 0 0 0 3 3h11" /><path d="M8 8l4 0" /><path d="M8 12l4 0" /><path d="M8 16l4 0" /></svg>
        <span style="font-size: 14px; font-weight: 500;">최신 뉴스레터 ${this.data.count}개 · ${layoutLabel}${categoryLabel}</span>
      </div>
      ${!this.readOnly ? '<p class="ejs-widget__hint" style="margin: 6px 0 0 0;">클릭하여 편집</p>' : ''}
    `;
  }

  async _renderEdit() {
    this.editing = true;
    await this._fetchCategories();

    const categoryOptions = this.categories
      .map(c => `<option value="${c.ext_id}" ${this.data.category_ext_id === c.ext_id ? 'selected' : ''}>${c.name}</option>`)
      .join('');

    this.wrapper.className = "ejs-widget ejs-widget--editing";
    this.wrapper.style.cssText = "";
    this.wrapper.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 12px;">
        <div>
          <label class="ejs-widget__label">레이아웃</label>
          <div style="display: flex; gap: 4px;">
            <button type="button" data-layout="responsive" class="ejs-widget__btn ${this.data.layout === 'responsive' ? 'ejs-widget__btn--active' : ''}">반응형</button>
            <button type="button" data-layout="list" class="ejs-widget__btn ${this.data.layout === 'list' ? 'ejs-widget__btn--active' : ''}">리스트</button>
            <button type="button" data-layout="grid" class="ejs-widget__btn ${this.data.layout === 'grid' ? 'ejs-widget__btn--active' : ''}">카드</button>
          </div>
        </div>
        <div>
          <label class="ejs-widget__label">카테고리</label>
          <select class="ejs-widget__input" data-category-select>
            <option value="">전체</option>
            ${categoryOptions}
          </select>
          <p style="font-size: 11px; color: #94a3b8; margin: 4px 0 0 0;">카테고리를 선택하면 해당 카테고리의 뉴스레터만 표시되며 섹션 제목이 추가됩니다.</p>
        </div>
        <div>
          <label class="ejs-widget__label">표시 개수</label>
          <input type="number" min="1" max="20" value="${this.data.count}" class="ejs-widget__input" style="width: 80px;">
          <p style="font-size: 11px; color: #94a3b8; margin: 4px 0 0 0;">카드/반응형은 최대 4열로 표시됩니다. 균일한 배치를 위해 4의 배수를 권장합니다.</p>
        </div>
      </div>
    `;

    const countInput = this.wrapper.querySelector("input[type=number]");
    countInput.addEventListener("input", () => {
      const val = parseInt(countInput.value, 10);
      if (val >= 1 && val <= 20) this.data.count = val;
    });

    const categorySelect = this.wrapper.querySelector("[data-category-select]");
    categorySelect.addEventListener("change", () => {
      const val = categorySelect.value;
      if (val) {
        const cat = this.categories.find(c => c.ext_id === val);
        this.data.category_ext_id = val;
        this.data.category_name = cat ? cat.name : null;
      } else {
        this.data.category_ext_id = null;
        this.data.category_name = null;
      }
    });

    this.wrapper.querySelectorAll("[data-layout]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.data.layout = btn.dataset.layout;
        this._renderEdit();
      });
    });

    setTimeout(() => countInput.focus(), 50);
  }

  save() { return this.data; }
  validate() { return true; }
}
